import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import buildInfo from '../buildInfo.json';
import { useModules } from '../contexts/ModulesContext';
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus';
import { PaywallModal } from './PaywallModal';
import { Button } from './ui/button';
import { ImpersonationBanner } from './ImpersonationBanner';
import { PaymentStatusBanner } from './PaymentStatusBanner';
import { TrialBanner } from './TrialBanner';
import { ProfileDropdown } from './ProfileDropdown';
import { NotificationBell } from './NotificationBell';
import axios from 'axios';
import { toast } from 'sonner';
import { PWAInstallBanner } from './PWAInstallBanner';
import { TawkToChat } from './TawkToChat'; // tawk.to entfernt – Zeile behalten für spätere Reaktivierung
import { PushNotificationPrompt } from './PushNotificationPrompt';
import { LieferungAddonCard } from './LieferungAddonCard';
import { BirthdayFireworks } from './BirthdayFireworks';

// ─── Module-Level Fetch Debounce ──────────────────────────────────────────────
// Außerhalb der React-Komponente → überlebt Unmount/Remount bei Seitennavigation.
// Verhindert, dass jede neue Seitenladung sofort 3-4 API-Calls triggert.
const _fetchDebounce = {};
const DEBOUNCE_MS = 45000; // 45 Sekunden Mindestabstand zwischen Fetches

function canFetch(key) {
  const now = Date.now();
  if (_fetchDebounce[key] && (now - _fetchDebounce[key]) < DEBOUNCE_MS) {
    return false; // Zu früh – letzter Fetch war vor weniger als 45s
  }
  _fetchDebounce[key] = now;
  return true;
}
// ─────────────────────────────────────────────────────────────────────────────
import {
  LayoutDashboard,
  MapPin,
  Users,
  CheckSquare,
  Clock,
  Package,
  Boxes,
  MessageSquare,
  MessageCircle,
  FileText,
  Folder as FolderIcon,
  Thermometer,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  Bell,
  ChevronDown,
  ChevronRight,
  User,
  Building,
  Lightbulb,
  ClipboardCheck,
  Globe,
  ClipboardMinus,
  ShoppingBag,
  FileDown,
  ClipboardList,
  BarChart3,
  ShoppingCart,
  BookOpen,
  Ticket,
  TestTube,
  PackageCheck,
  AlertCircle,
  HelpCircle,
  Truck,
  AlertTriangle,
  Wrench,
  Info,
  PackagePlus,
  Lock,
  Calendar,
  Fuel,
  Inbox,
  MonitorPlay,
  Tag,
  Table2,
  Settings as SettingsIcon,
  Zap,
  Flame,
  Droplets,
  FlaskConical,
  Cog,
  ArrowLeft,
  ShieldCheck,
  Shield,
  Sparkles,
  ArrowRightLeft,
  ShoppingBasket,
  GitMerge,
  TrendingDown,
} from 'lucide-react';
import { useInfoModules } from '../contexts/InfoModulesContext';

const API = process.env.REACT_APP_BACKEND_URL;

/** Map from icon slug (stored in DB) to Lucide component */
const INFO_ICON_MAP = {
  FileText,
  Table2,
  Info,
  Zap,
  Flame,
  Droplets,
  FlaskConical,
  Settings: SettingsIcon,
  Cog,
  BookOpen,
  BarChart3,
};

/**
 * Builds the Info-Dropdown children from the info_list_modules API response.
 * - Builtin "planogramme" module → routes to existing /admin/planogramme (unchanged)
 * - Type "pdf" module → routes to /admin/info/pdf/:moduleId
 * - Type "table" module → routes to /admin/info/table/:moduleId
 * - Adds "Module verwalten" entry for admins only.
 */
function buildInfoChildren(modules, userRole) {
  const activeModules = modules.filter(m => m.is_active);
  const children = activeModules.map(m => {
    const IconComp = INFO_ICON_MAP[m.icon] || FileText;
    const to = m.is_builtin && m.slug === 'planogramme'
      ? '/admin/planogramme'
      : m.is_meter_module
        ? `/admin/info/meter/${m.id}`
        : m.type === 'abscheider'
          ? `/admin/info/abscheider/${m.id}`
          : m.type === 'table'
            ? `/admin/info/table/${m.id}`
            : `/admin/info/pdf/${m.id}`;
    return { to, icon: IconComp, label: m.name, module: null };
  });

  // "Module verwalten" nur für Admin
  if (userRole === 'admin' || userRole === 'superadmin') {
    children.push({ to: '/admin/info/manage', icon: Cog, label: 'Module verwalten', module: null });
  }

  // Fallback: wenn keine Module geladen → statisch Planogramme zeigen
  if (activeModules.length === 0) {
    children.unshift({ to: '/admin/planogramme', icon: FileText, label: 'Planogramme', module: null });
  }

  return children;
}


// Industry-based module visibility map - defines which modules are shown per industry
// null = no restriction (all enabled modules are visible)
const INDUSTRY_MODULE_MAP = {
  tankstelle: ['mhd_control', 'backshop', 'tasks', 'temperature_control', 'general_writeoffs', 'bestellungen', 'lotto', 'unterweisungen', 'employee_management'],
  gastronomie: null,
  hotel: null,
  kiosk: null,
  einzelhandel: null,
  versicherungsmakler: ['employee_management'], // future industry
  sonstige: null,
  andere: null,
};

export const DashboardLayout = ({ children }) => {
  const { user, logout, restoreAuth, loading: authLoading } = useAuth();
  const { isModuleEnabled, loading: modulesLoading, enabledModules, isV2Catalog, moduleSource } = useModules();
  const { modules: infoModules } = useInfoModules();
  const navigate = useNavigate();
  const location = useLocation();
  const subscriptionStatus = useSubscriptionStatus();
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [birthdays, setBirthdays] = useState([]);         // bevorstehende Geburtstage
  const [showBirthdayModal, setShowBirthdayModal] = useState(false); // Heute-Modal
  const [showBirthdayFireworks, setShowBirthdayFireworks] = useState(false); // Raketen-Animation (Team)
  const [myBirthdayData, setMyBirthdayData] = useState(null); // eigener Geburtstag
  const [showMyBirthdayFireworks, setShowMyBirthdayFireworks] = useState(false); // eigene Animation
  const hasPaymentMethod = subscriptionStatus.hasPaymentMethod;
  const needsPayment = !subscriptionStatus.loading && !hasPaymentMethod;

  // Lieferung Add-on Upsell Modal
  const [showLieferungUpsell, setShowLieferungUpsell] = useState(false);

  // Get token for Notification System
  const token = localStorage.getItem('token');

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [fabOpen, setFabOpen] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [orderCategories, setOrderCategories] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSending, setFeedbackSending] = useState(false);
  const [unreadSuggestionsCount, setUnreadSuggestionsCount] = useState(0);
  const [pendingProductsCount, setPendingProductsCount] = useState(0);
  
  // Notifications State
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  
  // Impersonation State
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [impersonatedByRole, setImpersonatedByRole] = useState(null);
  
  // Current location name
  const [currentLocationName, setCurrentLocationName] = useState(null);
  
  // Dynamische Schichten für Sidebar
  const [dynamicSchichten, setDynamicSchichten] = useState([]);
  const [currentLocationId, setCurrentLocationId] = useState(null);

  // PWA-Update-Hinweis (kein Zwangs-Reload mehr – iOS-Upload-safe)
  useEffect(() => {
    const showUpdateToast = () => {
      toast.info('Neue Version verfügbar', {
        description: 'Tippe auf Aktualisieren, um die neueste Version zu laden.',
        duration: 15000,
        action: { label: 'Aktualisieren', onClick: () => window.location.reload() },
      });
    };
    if (window.__swUpdatePending) showUpdateToast();
    window.addEventListener('allflowx-sw-updated', showUpdateToast);
    return () => window.removeEventListener('allflowx-sw-updated', showUpdateToast);
  }, []);

  // Fetch current location name (first location) – NOT for superadmin
  useEffect(() => {
    const fetchCurrentLocation = async () => {
      // Superadmins haben KEINEN Standort – niemals anzeigen
      if (!user || user.role === 'superadmin') {
        setCurrentLocationName(null);
        setCurrentLocationId(null);
        return;
      }
      if (localStorage.getItem('token')) {
        try {
          // Use /user/locations for all roles (works for admin, stationsleitung, employee)
          const response = await axios.get(`${API}/api/user/locations`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          const locations = response.data || [];
          if (locations.length > 0) {
            setCurrentLocationName(locations[0].name);
            setCurrentLocationId(locations[0].id);
          } else {
            setCurrentLocationName(null);
            setCurrentLocationId(null);
          }
        } catch (error) {
          console.error('Failed to fetch location:', error);
          setCurrentLocationName(null);
          setCurrentLocationId(null);
        }
      }
    };

    fetchCurrentLocation();
  }, [user?.id, user?.role]);

  // Schichten für dynamische Sidebar laden
  useEffect(() => {
    const fetchSchichten = async () => {
      if (!currentLocationId || user?.role === 'superadmin') return;
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const r = await axios.get(`${API}/api/tasks/schichten?location_id=${currentLocationId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setDynamicSchichten(r.data || []);
      } catch {
        setDynamicSchichten([]);
      }
    };
    fetchSchichten();
  }, [currentLocationId, user?.role]);

  // Fetch order categories
  useEffect(() => {
    const fetchOrderCategories = async () => {
      if (user && localStorage.getItem('token')) {
        try {
          const response = await axios.get(`${API}/api/order-categories`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
          });
          setOrderCategories(response.data || []);
        } catch (error) {
          console.error('Failed to fetch order categories:', error);
          setOrderCategories([]);
        }
      }
    };

    fetchOrderCategories();
  }, [user?.id]);

  // Fetch unread suggestions count for admin
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user || user.role !== 'admin') return;
      if (!canFetch(`unreadCount_${user.id}`)) return; // Debounce: max alle 45s
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await axios.get(`${API}/api/improvement-suggestions/count/unread`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setUnreadSuggestionsCount(response.data?.count || 0);
      } catch (error) {
        console.error('Failed to fetch unread count:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 120000);
    return () => clearInterval(interval);
  }, [user?.id, user?.role]);

  // Support-Ticket-Antworten Badge (für Admin/Stationsleitung)
  useEffect(() => {
    if (!user || !['admin', 'stationsleitung'].includes(user.role)) return;
    const fetchSupportUnread = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/support/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
        setSupportUnreadCount(res.data?.count || 0);
      } catch {}
    };
    fetchSupportUnread();
    const iv = setInterval(fetchSupportUnread, 90000);
    return () => clearInterval(iv);
  }, [user?.id, user?.role]);

  // Fetch pending products count for superadmin sidebar badge
  const [reklamationCount, setReklamationCount] = useState(0);

  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!user || user.role !== 'superadmin') return;
      if (!canFetch(`pendingCount_${user.id}`)) return; // Debounce: max alle 45s
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const response = await axios.get(`${API}/api/superadmin/pending-products`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setPendingProductsCount((response.data || []).length);
      } catch (error) {
        console.error('Failed to fetch pending products count:', error);
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [user?.id, user?.role]);

  // Reklamations-Badge (für Admin + Stationsleitung)
  useEffect(() => {
    const fetchReklamationCount = async () => {
      if (!user || !['admin', 'stationsleitung'].includes(user.role)) return;
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const r = await axios.get(`${API}/api/lieferung/reklamation/count`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setReklamationCount(r.data?.count || 0);
      } catch { /* silent */ }
    };
    fetchReklamationCount();
    const interval = setInterval(fetchReklamationCount, 60000);
    return () => clearInterval(interval);
  }, [user?.id, user?.role]);

  // Fetch notifications for all roles
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      if (!canFetch(`notifications_${user.id}`)) return; // Debounce: max alle 45s
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        let endpoint = '';
        if (user.role === 'admin' || user.role === 'stationsleitung') {
          endpoint = `${API}/api/admin/notifications`;
        } else if (user.role === 'superadmin') {
          endpoint = `${API}/api/superadmin/notifications`;
        } else if (user.role === 'employee') {
          endpoint = `${API}/api/employee/notifications-legacy`;
        }
        if (endpoint) {
          const response = await axios.get(endpoint, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const unreadNotifications = (response.data || []).filter(n => !n.is_read);
          setNotifications(unreadNotifications);
          setUnreadNotificationsCount(unreadNotifications.length);
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
      }
    };

    fetchNotifications();
    const interval = setInterval(fetchNotifications, 120000);
    return () => clearInterval(interval);
  }, [user?.id, user?.role]);

  // ── Eigener Geburtstag (Geburtstagskind sieht persönliche Animation) ────────
  useEffect(() => {
    if (!user?.id) return;
    // Key: pro Geburtstags-Jahr (nicht pro Kalendertag) — so wird es auch bei verpasstem Geburtstag gezeigt
    const currentYear = new Date().getFullYear();
    const myBdayKey = `my_bday_seen_${currentYear}_${user.id}`;
    if (localStorage.getItem(myBdayKey)) return;

    const checkMyBirthday = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/my-birthday`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        if (data?.is_birthday) {
          setMyBirthdayData(data);
          setShowMyBirthdayFireworks(true);
          localStorage.setItem(myBdayKey, '1');
        }
      } catch { /* silent */ }
    };
    checkMyBirthday();
  }, [user?.id]);
  useEffect(() => {
    if (!user?.id || !user?.customer_id) return;

    const fetchBirthdays = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/birthdays?days_ahead=30`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = (res.data?.birthdays || []);
        setBirthdays(data); // Badge spiegelt immer die aktuellen Geburtstage
        // Auto-Popup nur für heute/die nächsten 3 Tage – das 30-Tage-Fenster füllt nur Glocke + Liste
        if (data.some(b => b.days_until <= 3)) {
          const hasToday = data.some(b => b.is_today);
          // Einmal pro Tag automatisch erinnern (auch an den Tagen davor)
          const todayKey = `bday_seen_${new Date().toISOString().slice(0, 10)}_${user.customer_id}`;
          if (!localStorage.getItem(todayKey)) {
            localStorage.setItem(todayKey, '1');
            if (hasToday) {
              setShowBirthdayFireworks(true); // Raketen, danach Modal (via onClose)
            } else {
              setShowBirthdayModal(true); // Bevorstehende Geburtstage direkt zeigen
            }
          }
        }
      } catch { /* silent */ }
    };
    fetchBirthdays();
  }, [user?.id, user?.customer_id]);

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    try {
      // Endpoint je nach Rolle – auch für Mitarbeiter
      let endpoint = '';
      if (user.role === 'employee') {
        endpoint = `${API}/api/employee/notifications/${notification.id}/read`;
      } else if (user.role === 'admin' || user.role === 'stationsleitung') {
        endpoint = `${API}/api/admin/notifications/${notification.id}/read`;
      } else if (user.role === 'superadmin') {
        endpoint = `${API}/api/superadmin/notifications/${notification.id}/read`;
      }
      
      // Als gelesen markieren (für alle Rollen)
      if (endpoint) {
        await axios.put(endpoint, {}, { 
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } 
        }).catch(() => {}); // Fehler ignorieren – Navigation soll trotzdem klappen
      }
      
      // Sofort aus lokalem State entfernen (Glocke aktualisiert sich sofort)
      setNotifications(prev => prev.filter(n => n.id !== notification.id));
      setUnreadNotificationsCount(prev => Math.max(0, prev - 1));
      
      // Close dropdown
      setShowNotifications(false);
      
      // Navigate based on notification type and user role
      const type = notification.type || '';
      
      // Employee notifications
      if (type === 'instruction_assigned' || type === 'instruction_reminder') {
        navigate('/employee/unterweisungen');
      }
      // BV notifications → BV-Verwaltung
      else if (type === 'bv_employee_signed' || type === 'bv_all_signed' || type === 'bv_reminder' || type === 'bv_reminder_admin' || type === 'betriebsvereinbarung_new') {
        if (user.role === 'admin' || user.role === 'superadmin' || user.role === 'stationsleitung') {
          navigate('/admin/betriebsvereinbarungen');
        } else {
          navigate('/profil/betriebsvereinbarungen');
        }
      }
      // Unterweisung-Signing → Admin geht zu Unterweisungen
      else if (type === 'instruction_signed' || type === 'instruction_all_signed') {
        if (user.role === 'admin' || user.role === 'superadmin' || user.role === 'stationsleitung') {
          navigate('/admin/unterweisungen');
        } else {
          navigate('/employee/unterweisungen');
        }
      }
      // Improvement suggestions (all types)
      else if (
        type === 'improvement_suggestion' || 
        type === 'improvement_suggestion_forwarded' ||
        type === 'suggestion_reply' ||
        type === 'suggestion_status_update' ||
        type === 'suggestion_archived' ||
        type === 'suggestion_created' ||
        type.includes('suggestion')
      ) {
        if (user.role === 'superadmin') {
          navigate('/superadmin/notifications');
        } else if (user.role === 'admin' || user.role === 'stationsleitung') {
          navigate('/admin/improvement-suggestions');
        } else if (user.role === 'employee') {
          navigate('/employee/verbesserungen');
        }
      }
      // Location-related notifications
      else if (type === 'location_updated' || type === 'location_created' || type === 'location_deleted' || type.includes('location')) {
        if (user.role === 'superadmin') {
          navigate('/superadmin/locations');
        }
      }
      // Employee-related notifications
      else if (type.includes('employee')) {
        if (user.role === 'superadmin' || user.role === 'admin') {
          navigate('/admin/employees');
        }
      }
      // Default: Navigate based on role
      else {
        console.log('Generic navigation for type:', type);
        if (user.role === 'superadmin') {
          navigate('/superadmin/notifications');
        } else if (user.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (user.role === 'stationsleitung' || user.role === 'employee') {
          navigate('/employee/dashboard');
        }
      }
    } catch (error) {
      console.error('Error handling notification:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      let endpoint = '';
      if (user.role === 'employee') {
        endpoint = `${API}/api/employee/notifications/mark-all-read`;
      } else if (user.role === 'admin' || user.role === 'stationsleitung') {
        endpoint = `${API}/api/admin/notifications/mark-all-read`;
      } else if (user.role === 'superadmin') {
        endpoint = `${API}/api/superadmin/notifications/mark-all-read`;
      }
      
      if (endpoint) {
        await axios.put(endpoint, {}, { 
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } 
        }).catch(() => {});
      }
      
      // Lokalen State sofort leeren (alle Benachrichtigungen weg)
      setNotifications([]);
      setUnreadNotificationsCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Check for impersonation on mount AND on location change
  useEffect(() => {
    const checkImpersonation = () => {
      // Check for impersonation
      const impersonationData = localStorage.getItem('impersonation');
      if (impersonationData) {
        try {
          const parsed = JSON.parse(impersonationData);
          setIsImpersonating(true);
          setImpersonatedUser(parsed);
          // Korrekte Rolle auslesen: admin-Impersonation speichert sie in impersonated_user.impersonated_by_role
          // Fallback: original_user.role (die Rolle desjenigen, der impersoniert) 
          setImpersonatedByRole(
            parsed.impersonated_user?.impersonated_by_role ||
            parsed.by ||
            parsed.original_user?.role ||
            'superadmin'
          );
        } catch (e) {
          console.error('Failed to parse impersonation data:', e);
          localStorage.removeItem('impersonation');
          setIsImpersonating(false);
          setImpersonatedUser(null);
          setImpersonatedByRole(null);
        }
      } else {
        setIsImpersonating(false);
        setImpersonatedUser(null);
        setImpersonatedByRole(null);
      }
    };
    
    checkImpersonation();
  }, [location.pathname]); // Re-check on every navigation

  // Handle exit impersonation
  const handleExitImpersonation = () => {
    try {
      const raw = localStorage.getItem('impersonation');
      if (!raw) {
        localStorage.removeItem('impersonation');
        localStorage.removeItem('impersonated_user');
        window.location.href = '/superadmin';
        return;
      }

      const impersonationData = JSON.parse(raw);
      const originalToken = impersonationData.originalToken || impersonationData.original_token;

      if (!originalToken) {
        localStorage.removeItem('impersonation');
        localStorage.removeItem('impersonated_user');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }

      // Token sofort in localStorage setzen (vor dem Reload)
      localStorage.setItem('token', originalToken);
      if (impersonationData.originalUser || impersonationData.original_user) {
        const u = impersonationData.originalUser || impersonationData.original_user;
        // originalUser kann bereits ein JSON-String ODER ein Objekt sein
        localStorage.setItem('user', typeof u === 'string' ? u : JSON.stringify(u));
      }
      localStorage.removeItem('impersonation');
      localStorage.removeItem('impersonated_user');

      // Vollständiger Reload – stellt sicher dass Auth-Context neu initialisiert wird
      const byRole = impersonationData.by || 'superadmin';
      window.location.href = byRole === 'superadmin' ? '/superadmin' : '/admin';

    } catch (error) {
      console.error('Fehler beim Beenden der Impersonation:', error);
      localStorage.removeItem('impersonation');
      localStorage.removeItem('impersonated_user');
      window.location.href = '/superadmin';
    }
  };

  const handleLogout = useCallback(() => {
    // Clear ALL impersonation data on logout
    localStorage.removeItem('impersonation');
    localStorage.removeItem('impersonated_user');
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const toggleDropdown = useCallback((key) => {
    setOpenDropdowns(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleSendFeedback = useCallback(async () => {
    if (!feedbackMessage.trim()) return;
    setFeedbackSending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message: feedbackMessage })
      });
      if (response.ok) {
        setFeedbackMessage('');
        setFeedbackOpen(false);
        alert('Vielen Dank für Ihren Vorschlag!');
      }
    } catch (error) {
      console.error('Feedback error:', error);
    } finally {
      setFeedbackSending(false);
    }
  }, [feedbackMessage]);

  // Memoized Navigation Arrays - WICHTIG: Werden nur einmal erstellt
  // Superadmin Navigation
  const superadminLinks = useMemo(() => [
    // ── GLOBAL ──────────────────────────────────────────────────────
    { type: 'section', label: 'GLOBAL' },
    { to: '/superadmin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/superadmin/statistics', icon: BarChart3, label: 'Statistik' },
    { to: '/superadmin/support', icon: Ticket, label: 'Support-Tickets' },
    { to: '/superadmin/products', icon: PackageCheck, label: 'Produktverwaltung', badge: pendingProductsCount > 0 ? pendingProductsCount : null },
    { to: '/superadmin/backshop-merge', icon: GitMerge, label: 'Backshop Merge-Queue' },
    { to: '/superadmin/ean-cleanup', icon: Wrench, label: 'EAN-Bereinigung' },
    { to: '/superadmin/module-management', icon: Boxes, label: 'Modul-Verwaltung' },
    { to: '/superadmin/affiliates', icon: Users, label: 'Affiliates' },
    { to: '/superadmin/help-center', icon: HelpCircle, label: 'Hilfe-Verwaltung' },
    { to: '/superadmin/data-integrity', icon: ShieldCheck, label: 'Datenqualität' },
    // ── BRANCHEN ──────────────────────────────────────────────────────
    { type: 'section', label: 'BRANCHEN' },
    { to: '/superadmin/industry/tankstelle', icon: Fuel, label: 'Tankstellen-Hub' },
    // ── STRIPE ────────────────────────────────────────────────────────
    { type: 'section', label: 'STRIPE' },
    { to: '/superadmin/stripe', icon: CreditCard, label: 'Stripe Control Center' },
    // ── LIZENZ ────────────────────────────────────────────────────────
    { type: 'section', label: 'LIZENZ' },
    { to: '/superadmin/license', icon: Shield, label: 'Lizenz-Diagnose' },
    { to: '/superadmin/billing-v2-checkout-test', icon: CreditCard, label: 'V2 Testcheckout' },
    // ── SALES TOOLS ────────────────────────────────────────────────────
    { type: 'section', label: 'SALES' },
    { to: '/superadmin/demo-sessions', icon: MonitorPlay, label: 'Demo-System' },
    { to: '/superadmin/gutscheine', icon: Tag, label: 'Gutschein-Zentrale' },
  ], [pendingProductsCount]);

  // Admin (Tankstellenpächter) Navigation with Dropdowns
  const adminNavigation = useMemo(() => {
    // Dynamische Schicht-Links
    const baseNav = [
      { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', module: null },
      {
        key: 'aufgaben',
        icon: CheckSquare,
        label: 'Aufgaben',
        isDropdown: true,
        module: 'tasks',
        children: [
          { to: '/aufgaben', icon: ClipboardCheck, label: 'Tagesgeschäft', module: 'tasks' },
          { to: '/aufgaben/auffuellen', icon: ShoppingCart, label: 'Auffüllen', module: 'tasks' },
          { to: '/aufgaben/reinigung', icon: Sparkles, label: 'Reinigungsplan', module: 'tasks' },
          {
            key: 'uebergabe-placeholder',
            label: 'Übergabe',
            icon: ArrowRightLeft,
            isPlaceholder: true,
            placeholderText: 'Bald verfügbar',
            module: 'tasks',
          },
          {
            key: 'reporting',
            icon: BarChart3,
            label: 'Reporting',
            isSubDropdown: true,
            module: null,
            subChildren: [
              { to: '/tresor', icon: Lock, label: 'Tresor', module: 'tasks' },
              { to: '/admin/lotto', icon: Ticket, label: 'Rubellose', module: 'lotto' },
              { to: '/admin/zusatz', icon: PackagePlus, label: 'Zusatz', module: null },

              { to: '/admin/wegfahrer', icon: Fuel, label: 'Wegfahrer', module: 'tasks' },
              { to: '/admin/reporting/mitarbeiter-minus', icon: TrendingDown, label: 'Mitarbeiter-Minus', module: 'tasks' },
              { to: '/admin/schichtabrechnung', icon: BarChart3, label: 'Schichtabrechnung', module: 'tasks' },
            ]
          },
          {
            key: 'temperatur',
            icon: Thermometer,
            label: 'Temperatur',
            isSubDropdown: true,
            module: 'temperature_control',
            subChildren: [
              { to: '/admin/temperature', icon: Thermometer, label: 'Temperatur-Messpunkte', module: 'temperature_control' },
              { to: '/admin/temperature-input', icon: Thermometer, label: 'Temperatureingabe', module: 'temperature_control' },
              { to: '/admin/temperature-control', icon: ClipboardCheck, label: 'Kontrolle der Temperatur', module: 'temperature_control' },
            ]
          },
        ]
      },
      { to: '/admin/produkte', icon: Package, label: 'Produkte', module: null },
      {
        key: 'backshop',
        icon: ShoppingBag,
        label: 'Backshop',
        isDropdown: true,
        module: 'backshop',
        children: [
          { to: '/admin/abschriften/backshop-liste', icon: ShoppingBag, label: 'Backshop Liste', module: 'backshop' },
          { to: '/admin/abschriften/backshop', icon: ShoppingBag, label: 'Backshop Abschriften', module: 'backshop' },
          { to: '/admin/backshop-belegung', icon: ShoppingBag, label: 'Handbuch', module: 'backshop' },
          { to: '/admin/backshop/vorschlaege', icon: Inbox, label: 'Produkt-Vorschläge', module: 'backshop', hideForRoles: ['employee'] },
          { to: '/admin/tgtg', icon: ShoppingBag, label: 'TGTG', module: 'backshop' },
        ]
      },
      {
        key: 'allgemein',
        icon: ClipboardList,
        label: 'Abschriften',
        isDropdown: true,
        module: 'general_writeoffs',
        children: [
          { to: '/admin/mhd-control', icon: Package, label: 'MHD', module: 'mhd_control' },
          { to: '/admin/allgemein-typen', icon: Settings, label: 'Abschrift-Typen', module: 'general_writeoffs', adminOnly: true },
          { to: '/employee/abschriften/allgemein', icon: ClipboardList, label: 'Abschriften erfassen', module: 'general_writeoffs' },
        ]
      },
      {
        key: 'bestellungen',
        icon: ShoppingCart,
        label: 'Bestellungen',
        isDropdown: true,
        module: 'bestellungen',
        children: [
          { 
            to: '/admin/bestell-kategorien', 
            icon: Settings, 
            label: 'Kategorien verwalten', 
            module: 'bestellungen',
            adminOnly: true 
          },
          { 
            to: '/admin/bestell-produkte', 
            icon: Package, 
            label: 'Produkte verwalten', 
            module: 'bestellungen',
            adminOnly: true 
          },
          ...orderCategories.map(cat => ({
            to: `/bestellungen/${cat.id}`,
            icon: ShoppingCart,
            label: cat.name,
            emoji: cat.icon,
            color: cat.color,
            module: 'bestellungen'
          })),
          { 
            to: '/bestellungen/historie', 
            icon: ClipboardList, 
            label: 'Bestellhistorie', 
            module: 'bestellungen'
          },

          // Lieferung Add-on: immer sichtbar, aber gesperrt wenn inaktiv
          ...([
            { to: '/lieferung', icon: Truck, label: 'Lieferung', module: null },
            { to: '/lieferung/kalender', icon: Calendar, label: 'Kalender', module: null },
            { to: '/lieferung/reklamation', icon: AlertTriangle, label: 'Reklamation', module: null, badge: reklamationCount || null },
          ].map(item => isModuleEnabled('lieferung') ? item : { ...item, lieferungLocked: true })),
        ]
      },
      {
        to: '/einkaufsliste',
        icon: ShoppingBasket,
        label: 'Einkaufsliste',
        module: 'tasks',
      },
      {
        key: 'unterweisungen',
        icon: BookOpen,
        label: 'Unterweisungen',
        to: '/admin/unterweisungen',
        module: 'unterweisungen'
      },
      {
        key: 'exports',
        icon: FileDown,
        label: 'Exports',
        isDropdown: true,
        module: null,
        children: [
          { to: '/admin/temperature-export', icon: Thermometer, label: 'Temperatur', module: 'temperature_control' },
          { to: '/admin/mhd-export', icon: Package, label: 'MHD', module: 'mhd_control' },
          { to: '/admin/backshop-export', icon: ShoppingBag, label: 'Backshop', module: 'backshop' },
          { to: '/admin/bestellungen-export', icon: ShoppingCart, label: 'Bestellungen', module: 'bestellungen' },
          { to: '/admin/allgemein-export', icon: ClipboardList, label: 'Allgemein', module: 'general_writeoffs' },
          { to: '/admin/unterweisungen/export', icon: BookOpen, label: 'Unterweisung', module: 'unterweisungen' },
          { to: '/admin/task-report', icon: CheckSquare, label: 'Aufgaben', module: 'tasks' },
        ]
      },
      // NOTE: "Module & Add-ons" removed - Module werden pro Standort verwaltet unter "Standorte"
      {
        key: 'info',
        icon: Info,
        label: 'Info',
        isDropdown: true,
        module: null,
        children: buildInfoChildren(infoModules, user?.role)
      },
      {
        key: 'inventur',
        icon: ClipboardList,
        label: 'Inventur',
        isDropdown: true,
        module: null,
        children: [
          { to: '/inventur/presse', icon: ClipboardList, label: 'Presse', module: 'press_inventory' },
          { to: '/inventur/manuell', icon: ClipboardList, label: 'Manuell', module: 'manual_inventory' },
        ]
      },
      // Planogramme ist über buildInfoChildren (Info-Dropdown) eingebunden — kein separater Eintrag nötig
      { to: '/admin/statistik', icon: BarChart3, label: 'Statistik', module: null },
      { to: '/help', icon: HelpCircle, label: 'Hilfe-Center', module: null },
    ];

    // While loading modules OR wenn Module noch leer sind (Race Condition Guard):
    // Zeige immer die volle Nav bis Module definitiv geladen sind.
    // Verhindert den kurzzeitigen Sidebar-Kollaps wenn loading=false aber enabledModules=[]
    if (modulesLoading || enabledModules.length === 0) {
      return baseNav;
    }

    // Industry-based module restrictions (uses shared INDUSTRY_MODULE_MAP constant)
    // Für Billing-V2-Kunden (isV2Catalog=true): Module kommen aus Billing Catalog → kein Industry-Filter
    // Für location_configs-Kunden: enabled_modules aus location config ist SSOT → kein Industry-Filter nötig
    // Industry-Filter NUR für Kunden ohne explizite Modul-Konfiguration (z.B. unknown/default source)
    const industry = user?.industry || null;
    const skipIndustryFilter = isV2Catalog || moduleSource === 'location_configs' || moduleSource === 'superadmin' || moduleSource === 'demo';
    const allowedModules = (!skipIndustryFilter && industry) ? (INDUSTRY_MODULE_MAP[industry] ?? null) : null;

    const isModuleVisibleForIndustry = (moduleKey) => {
      if (!allowedModules) return true; // no restriction (V2 oder Branchen ohne Map)
      if (!moduleKey) return true; // items without a module key are always visible
      return allowedModules.includes(moduleKey);
    };

    // 🆕 UX FIX: Only show ENABLED modules - deaktivierte Module komplett entfernen
    return baseNav
      .filter(item => {
        // Dashboard & Statistik & Items ohne Modul sind immer sichtbar
        if (!item.module) return true;
        // alwaysShow items (z.B. Lieferung) bleiben immer sichtbar (aber ggf. disabled)
        if (item.alwaysShow) return true;
        // Nur Module anzeigen, die aktiviert sind UND für die Branche sichtbar sind
        return isModuleEnabled(item.module) && isModuleVisibleForIndustry(item.module);
      })
      .map(item => {
        // alwaysShow + Modul nicht aktiv → als locked markieren
        if (item.alwaysShow && item.module && !isModuleEnabled(item.module)) {
          return { ...item, addonLocked: true };
        }

        // Wenn Dropdown: Filter auch die Kinder (IMMUTABLE)
        if (item.isDropdown && item.children) {
          const filteredChildren = item.children.filter(child => {
            if (!child.module) return true; // Fallback für Items ohne Modul
            return isModuleEnabled(child.module) && isModuleVisibleForIndustry(child.module);
          });
          
          // Nur Dropdown anzeigen, wenn mindestens 1 Kind sichtbar ist
          if (filteredChildren.length === 0) {
            return null;
          }
          
          return { ...item, children: filteredChildren };
        }
        
        return item;
      })
      .filter(item => item !== null); // Remove nulls
  }, [isModuleEnabled, modulesLoading, enabledModules, orderCategories, unreadSuggestionsCount, user, reklamationCount, infoModules, dynamicSchichten, moduleSource]);

  // Employee Navigation with Dropdowns - Memoized
  const employeeNavigation = useMemo(() => {
    // Dynamische Schicht-Links für Mitarbeiter
    const baseNav = [
      { to: '/employee', icon: LayoutDashboard, label: 'Dashboard', module: null },
      {
        key: 'aufgaben',
        icon: CheckSquare,
        label: 'Aufgaben',
        isDropdown: true,
        module: 'tasks',
        children: [
          { to: '/aufgaben', icon: ClipboardCheck, label: 'Tagesgeschäft', module: 'tasks' },
          { to: '/aufgaben/auffuellen', icon: ShoppingCart, label: 'Auffüllen', module: 'tasks' },
          { to: '/aufgaben/reinigung', icon: Sparkles, label: 'Reinigungsplan', module: 'tasks' },
          { key: 'uebergabe-emp', label: 'Übergabe', icon: ArrowRightLeft, isPlaceholder: true, placeholderText: 'Bald verfügbar', module: 'tasks' },
          {
            key: 'reporting',
            icon: BarChart3,
            label: 'Reporting',
            isSubDropdown: true,
            module: null,
            subChildren: [
              { to: '/tresor', icon: Lock, label: 'Tresor', module: 'tasks' },
              { to: '/employee/lotto', icon: Ticket, label: 'Rubellose', module: 'lotto' },
              { to: '/employee/zusatz', icon: PackagePlus, label: 'Zusatz', module: null },
              { to: '/wegfahrer/neu', icon: Fuel, label: 'Wegfahrer', module: 'tasks' },
              { to: '/meine-abrechnung', icon: TrendingDown, label: 'Meine Abrechnung', module: 'tasks' },
            ]
          },
          {
            key: 'temperatur',
            icon: Thermometer,
            label: 'Temperatur',
            isSubDropdown: true,
            module: 'temperature_control',
            subChildren: [
              { to: '/employee/temperature', icon: Thermometer, label: 'Temperatur-Eingabe', module: 'temperature_control' },
              { to: '/employee/temperature-control', icon: ClipboardCheck, label: 'Kontrolle der Temperatur', module: 'temperature_control' },
            ]
          },
        ],
      },
      {
        key: 'backshop',
        icon: ShoppingBag,
        label: 'Backshop',
        isDropdown: true,
        module: 'backshop',
        children: [
          { to: '/employee/abschriften/backshop', icon: ShoppingBag, label: 'Backshop Abschriften', module: 'backshop' },
          { to: '/admin/backshop-belegung', icon: ShoppingBag, label: 'Handbuch', module: 'backshop' },
          { to: '/admin/tgtg', icon: ShoppingBag, label: 'TGTG', module: 'backshop' },
        ]
      },
      {
        key: 'allgemeine_abschriften',
        icon: Package,
        label: 'Abschriften',
        isDropdown: true,
        module: 'general_writeoffs',
        children: [
          { to: '/employee/mhd-control', icon: Package, label: 'MHD', module: 'mhd_control' },
          { to: '/employee/abschriften/allgemein', icon: ClipboardList, label: 'Abschriften erfassen', module: 'general_writeoffs' },
        ]
      },
      {
        key: 'bestellungen',
        icon: ShoppingCart,
        label: 'Bestellungen',
        isDropdown: true,
        module: 'bestellungen',
        children: [
          ...orderCategories.map(cat => ({
            to: `/bestellungen/${cat.id}`,
            icon: ShoppingCart,
            label: cat.name,
            emoji: cat.icon,
            color: cat.color,
            module: 'bestellungen'
          })),
          { 
            to: '/bestellungen/historie', 
            icon: ClipboardList, 
            label: 'Bestellhistorie', 
            module: 'bestellungen'
          },

          // Lieferung Add-on: immer sichtbar, aber gesperrt wenn inaktiv
          ...(isModuleEnabled('lieferung')
            ? [{ to: '/lieferung', icon: Truck, label: 'Lieferung', module: null }]
            : [{ to: '/lieferung', icon: Truck, label: 'Lieferung', module: null, lieferungLocked: true }]
          ),
        ]
      },
      {
        to: '/einkaufsliste',
        icon: ShoppingBasket,
        label: 'Einkaufsliste',
        module: 'tasks',
      },
      {
        key: 'unterweisungen',
        icon: BookOpen,
        label: 'Unterweisungen',
        to: '/employee/unterweisungen',
        module: 'unterweisungen'
      },
      {
        key: 'dokumente',
        icon: FolderIcon,
        label: 'Meine Dokumente',
        to: '/employee/dokumente',
        module: null,
      },
      {
        key: 'info',
        icon: Info,
        label: 'Info',
        isDropdown: true,
        module: null,
        children: buildInfoChildren(infoModules, user?.role)
      },
      { to: '/help', icon: HelpCircle, label: 'Hilfe-Center', module: null },
    ];

    // While loading modules, show all navigation (Race Condition Guard)
    if (modulesLoading || enabledModules.length === 0) {
      return baseNav;
    }

    // Industry-based module restrictions (uses shared INDUSTRY_MODULE_MAP constant)
    const industry = user?.industry || null;
    const allowedModules = industry ? (INDUSTRY_MODULE_MAP[industry] ?? null) : null;
    const isModuleVisibleForIndustry = (moduleKey) => {
      if (!allowedModules || !moduleKey) return true;
      return allowedModules.includes(moduleKey);
    };

    // Filter navigation based on enabled modules (after loading)
    return baseNav.map(item => {
      // Filter dropdowns with children
      if (item.isDropdown && item.children) {
        // Check if dropdown itself requires a module
        if (item.module && (!isModuleEnabled(item.module) || !isModuleVisibleForIndustry(item.module))) {
          return null;
        }
        
        // Filter children based on their modules
        item.children = item.children.filter(child => {
          if (child.module) {
            return isModuleEnabled(child.module) && isModuleVisibleForIndustry(child.module);
          }
          return true; // Show if no module specified
        });
        
        // Only show dropdown if at least one child is visible
        return item.children.length > 0 ? item : null;
      }
      
      // For non-dropdown items, check module
      if (item.module && (!isModuleEnabled(item.module) || !isModuleVisibleForIndustry(item.module))) {
        return null;
      }
      
      return item;
    }).filter(Boolean); // Remove null items
  }, [isModuleEnabled, modulesLoading, enabledModules, orderCategories, user, infoModules, dynamicSchichten]);

  // Stationsleitung Navigation - basiert auf Employee, aber mit Admin-Level Backshop, Lotto, Exports
  const stationsleitungNavigation = useMemo(() => {
    // Dynamische Schicht-Links für Stationsleitung
    const baseNav = [
      { to: '/employee/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: null },
      {
        key: 'aufgaben',
        icon: CheckSquare,
        label: 'Aufgaben',
        isDropdown: true,
        module: 'tasks',
        children: [
          { to: '/aufgaben', icon: ClipboardCheck, label: 'Tagesgeschäft', module: 'tasks' },
          { to: '/aufgaben/auffuellen', icon: ShoppingCart, label: 'Auffüllen', module: 'tasks' },
          { to: '/aufgaben/reinigung', icon: Sparkles, label: 'Reinigungsplan', module: 'tasks' },
          { key: 'uebergabe-sl', label: 'Übergabe', icon: ArrowRightLeft, isPlaceholder: true, placeholderText: 'Bald verfügbar', module: 'tasks' },
          {
            key: 'reporting',
            icon: BarChart3,
            label: 'Reporting',
            isSubDropdown: true,
            module: null,
            subChildren: [
              { to: '/tresor', icon: Lock, label: 'Tresor', module: 'tasks' },
              { to: '/admin/lotto', icon: Ticket, label: 'Rubellose', module: 'lotto' },
              { to: '/admin/zusatz', icon: PackagePlus, label: 'Zusatz', module: null },
              { to: '/admin/wegfahrer', icon: Fuel, label: 'Wegfahrer', module: 'tasks' },
              { to: '/admin/reporting/mitarbeiter-minus', icon: TrendingDown, label: 'Mitarbeiter-Minus', module: 'tasks' },
              { to: '/admin/schichtabrechnung', icon: BarChart3, label: 'Schichtabrechnung', module: 'tasks' },
            ]
          },
          {
            key: 'temperatur',
            icon: Thermometer,
            label: 'Temperatur',
            isSubDropdown: true,
            module: 'temperature_control',
            subChildren: [
              { to: '/employee/temperature', icon: Thermometer, label: 'Temperatur-Eingabe', module: 'temperature_control' },
              { to: '/employee/temperature-control', icon: ClipboardCheck, label: 'Kontrolle der Temperatur', module: 'temperature_control' },
            ]
          },
        ]
      },
      {
        key: 'backshop',
        icon: ShoppingBag,
        label: 'Backshop',
        isDropdown: true,
        module: 'backshop',
        children: [
          { to: '/admin/abschriften/backshop-liste', icon: ShoppingBag, label: 'Backshop Liste', module: 'backshop' },
          { to: '/admin/abschriften/backshop', icon: ShoppingBag, label: 'Backshop Abschriften', module: 'backshop' },
          { to: '/admin/backshop-belegung', icon: ShoppingBag, label: 'Handbuch', module: 'backshop' },
          { to: '/admin/backshop/vorschlaege', icon: Inbox, label: 'Produkt-Vorschläge', module: 'backshop', hideForRoles: ['employee'] },
          { to: '/admin/tgtg', icon: ShoppingBag, label: 'TGTG', module: 'backshop' },
        ]
      },

      {
        key: 'allgemeine_abschriften',
        icon: ClipboardMinus,
        label: 'Abschriften',
        isDropdown: true,
        module: 'general_writeoffs',
        children: [
          { to: '/employee/mhd-control', icon: Package, label: 'MHD', module: 'mhd_control' },
          { to: '/employee/abschriften/allgemein', icon: ClipboardList, label: 'Abschriften erfassen', module: 'general_writeoffs' },
        ]
      },
      {
        key: 'bestellungen',
        icon: ShoppingCart,
        label: 'Bestellungen',
        isDropdown: true,
        module: 'bestellungen',
        children: [
          ...orderCategories.map(cat => ({
            to: `/bestellungen/${cat.id}`,
            icon: ShoppingCart,
            label: cat.name,
            emoji: cat.icon,
            color: cat.color,
            module: 'bestellungen'
          })),
          { 
            to: '/bestellungen/historie', 
            icon: ClipboardList, 
            label: 'Bestellhistorie', 
            module: 'bestellungen'
          },

          // Lieferung Add-on: immer sichtbar, aber gesperrt wenn inaktiv
          ...([
            { to: '/lieferung', icon: Truck, label: 'Lieferung', module: null },
            { to: '/lieferung/kalender', icon: Calendar, label: 'Kalender', module: null },
            { to: '/lieferung/reklamation', icon: AlertTriangle, label: 'Reklamation', module: null, badge: reklamationCount || null },
          ].map(item => isModuleEnabled('lieferung') ? item : { ...item, lieferungLocked: true })),
        ]
      },
      {
        to: '/einkaufsliste',
        icon: ShoppingBasket,
        label: 'Einkaufsliste',
        module: 'tasks',
      },
      {
        key: 'unterweisungen',
        icon: BookOpen,
        label: 'Unterweisungen',
        to: '/employee/unterweisungen',
        module: 'unterweisungen'
      },
      {
        key: 'dokumente',
        icon: FolderIcon,
        label: 'Meine Dokumente',
        to: '/employee/dokumente',
        module: null,
      },
      // Exports wie Admin (vollständig)
      {
        key: 'exports',
        icon: FileDown,
        label: 'Exports',
        isDropdown: true,
        module: null,
        children: [
          { to: '/admin/temperature-export', icon: Thermometer, label: 'Temperatur', module: 'temperature_control' },
          { to: '/admin/mhd-export', icon: Package, label: 'MHD', module: 'mhd_control' },
          { to: '/admin/backshop-export', icon: ShoppingBag, label: 'Backshop', module: 'backshop' },
          { to: '/admin/bestellungen-export', icon: ShoppingCart, label: 'Bestellungen', module: 'bestellungen' },
          { to: '/admin/allgemein-export', icon: ClipboardList, label: 'Allgemein', module: 'general_writeoffs' },
          { to: '/admin/unterweisungen/export', icon: BookOpen, label: 'Unterweisung', module: 'unterweisungen' },
        ]
      },
      {
        key: 'info',
        icon: Info,
        label: 'Info',
        isDropdown: true,
        module: null,
        children: buildInfoChildren(infoModules, user?.role)
      },
      {
        key: 'inventur',
        icon: ClipboardList,
        label: 'Inventur',
        isDropdown: true,
        module: null,
        children: [
          { to: '/inventur/presse', icon: ClipboardList, label: 'Presse', module: 'press_inventory' },
          { to: '/inventur/manuell', icon: ClipboardList, label: 'Manuell', module: 'manual_inventory' },
        ]
      },
      { to: '/admin/statistik', icon: BarChart3, label: 'Statistik', module: null },
      { to: '/help', icon: HelpCircle, label: 'Hilfe-Center', module: null },
    ];

    if (modulesLoading || enabledModules.length === 0) {
      return baseNav;
    }

    return baseNav.map(item => {
      if (item.isDropdown && item.children) {
        if (item.module && !isModuleEnabled(item.module)) {
          return null;
        }
        const filteredChildren = item.children.filter(child => {
          if (child.module) return isModuleEnabled(child.module);
          return true;
        });
        return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
      }
      if (item.module && !isModuleEnabled(item.module)) {
        return null;
      }
      return item;
    }).filter(Boolean);
  }, [isModuleEnabled, modulesLoading, enabledModules, orderCategories, reklamationCount, infoModules, dynamicSchichten, user]);

  // Effective role (auch außerhalb von navigation useMemo nutzbar)
  const effectiveRole = isImpersonating
    ? (impersonatedUser?.impersonated_user?.role
       || impersonatedUser?.role
       || user?.role)
    : user?.role;

  // Memoized navigation getter
  // Im Impersonation-Modus: Sidebar zeigt Navigation des impersonierten Nutzers
  // authLoading=true: user noch nicht geladen → adminNavigation als Default (verhindert Flackern
  // bei Admin-only-Einträgen wie "Produkte" die in employeeNavigation nicht existieren)
  const navigation = useMemo(() => {
    if (!effectiveRole || authLoading) return { type: 'dropdown', links: adminNavigation };
    if (effectiveRole === 'superadmin') return { type: 'simple', links: superadminLinks };
    if (effectiveRole === 'admin') return { type: 'dropdown', links: adminNavigation };
    if (effectiveRole === 'stationsleitung') return { type: 'dropdown', links: stationsleitungNavigation };
    // Employee (und alle anderen) → Mitarbeiter-Navigation
    return { type: 'dropdown', links: employeeNavigation };
  }, [effectiveRole, authLoading, superadminLinks, adminNavigation, employeeNavigation, stationsleitungNavigation]);

  // Memoized render functions
  const renderSimpleLinks = useCallback((links) => (
    links.map((link, idx) => {
      // Section-Header für GLOBAL / BRANCHEN
      if (link.type === 'section') {
        return (
          <div
            key={`section-${link.label}`}
            className={`px-4 py-1 ${idx > 0 ? 'mt-4' : 'mt-0'} mb-0.5`}
          >
            <p className="text-xs font-bold text-muted-foreground/60 uppercase tracking-widest">
              {link.label}
            </p>
          </div>
        );
      }

      const Icon = link.icon;
      const isActive = location.pathname === link.to;
      
      const handleClick = () => {
        setSidebarOpen(false);
      };
      
      return (
        <Link
          key={link.to}
          to={link.to}
          onClick={handleClick}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
            ${isActive 
              ? 'bg-primary/10 text-primary font-medium' 
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
          `}
        >
          <Icon className="h-5 w-5" />
          <span className="font-medium">{link.label}</span>
          {link.badge && link.badge > 0 && (
            <span className="ml-auto inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
              {link.badge > 9 ? '9+' : link.badge}
            </span>
          )}
        </Link>
      );
    })
  ), [location.pathname]);

  // Check if any child or subchild is active
  const hasActiveChildOrSubChild = (item) => {
    if (!item.children) return false;
    return item.children.some(child => {
      if (child.to && location.pathname === child.to) return true;
      if (child.isSubDropdown && child.subChildren) {
        return child.subChildren.some(sub => location.pathname === sub.to);
      }
      return false;
    });
  };

  const renderDropdownLinks = (links) => (
    links.map((item) => {
      if (item.isDropdown) {
        const Icon = item.icon;
        const isOpen = openDropdowns[item.key];
        const hasActiveChild = hasActiveChildOrSubChild(item);

        // addonLocked: immer sichtbar, aber grau → Klick → /admin/locations (Upsell)
        if (item.addonLocked) {
          return (
            <div key={item.key}>
              <button
                onClick={() => { navigate('/admin/locations'); setSidebarOpen(false); }}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-muted-foreground/50 hover:bg-muted/50 cursor-pointer"
                title="Lieferschein-KI-Manager jetzt aktivieren"
                data-testid="sidebar-lieferung-locked"
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                </div>
                <span className="text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground/60">Add-on</span>
              </button>
            </div>
          );
        }

        return (
          <div key={item.key}>
            <button
              onClick={() => {
                toggleDropdown(item.key);
              }}
              className={`
                w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all duration-200
                ${hasActiveChild 
                  ? 'bg-primary/10 text-primary font-medium' 
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
              `}
            >
              <div className="flex items-center gap-3">
                <Icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </div>
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </div>
            </button>
            
            {isOpen && (
              <div className="ml-4 mt-1 space-y-1 border-l border-border pl-4">
                {item.children.filter(child =>
                  !child.hideForRoles || !child.hideForRoles.includes(effectiveRole)
                ).map((child) => {
                  // Check if this is a sub-dropdown
                  if (child.isPlaceholder) {
                    const PlaceholderIcon = child.icon;
                    return (
                      <div key={child.key}
                        className="flex items-center justify-between px-3 py-2 rounded-lg text-sm text-muted-foreground/50 cursor-not-allowed"
                        title={child.placeholderText || 'Bald verfügbar'}>
                        <div className="flex items-center gap-3">
                          <PlaceholderIcon className="h-4 w-4" />
                          <span>{child.label}</span>
                        </div>
                        <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full">Bald</span>
                      </div>
                    );
                  }

                  if (child.isSubDropdown) {
                    const SubIcon = child.icon;
                    const isSubOpen = openDropdowns[child.key];
                    const hasActiveSubChild = child.subChildren?.some(sub => location.pathname === sub.to);
                    
                    return (
                      <div key={child.key}>
                        <button
                          onClick={() => toggleDropdown(child.key)}
                          className={`
                            w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm
                            ${hasActiveSubChild 
                              ? 'bg-primary/10 text-primary font-medium' 
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <SubIcon className="h-4 w-4" />
                            <span>{child.label}</span>
                          </div>
                          {isSubOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                        </button>
                        
                        {isSubOpen && (
                          <div className="ml-4 mt-1 space-y-1 border-l border-border pl-3">
                            {child.subChildren.map((subChild) => {
                              const SubChildIcon = subChild.icon;
                              const isActive = location.pathname === subChild.to;
                              return (
                                <Link
                                  key={subChild.to}
                                  to={subChild.to}
                                  onClick={() => setSidebarOpen(false)}
                                  className={`
                                    flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 text-xs
                                    ${isActive 
                                      ? 'bg-primary/10 text-primary font-medium' 
                                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                                  `}
                                >
                                  <SubChildIcon className="h-3 w-3" />
                                  <span>{subChild.label}</span>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                  
                  // Regular child link
                  const ChildIcon = child.icon;
                  const isActive = location.pathname === child.to;

                  // lieferungLocked: gegraut, kein Hover, Klick → Upsell Modal
                  if (child.lieferungLocked) {
                    return (
                      <button
                        key={child.to}
                        onClick={() => { setShowLieferungUpsell(true); setSidebarOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm opacity-50 cursor-pointer"
                        data-testid={`sidebar-lieferung-locked-${child.label}`}
                        title="Lieferschein-KI-Manager aktivieren"
                      >
                        <ChildIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">{child.label}</span>
                        <span className="ml-auto text-xs bg-muted px-1.5 py-0.5 rounded text-muted-foreground/60">Add-on</span>
                      </button>
                    );
                  }

                  return (
                    <Link
                      key={child.to}
                      to={child.to}
                      onClick={() => setSidebarOpen(false)}
                      className={`
                        flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 text-sm
                        ${isActive 
                          ? 'bg-primary/10 text-primary font-medium' 
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
                      `}
                    >
                      {child.emoji ? (
                        <span className="text-lg">{child.emoji}</span>
                      ) : (
                        <ChildIcon className="h-4 w-4" />
                      )}
                      <span>{child.label}</span>
                      {child.badge > 0 && (
                        <span className="ml-auto inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full">
                          {child.badge > 9 ? '9+' : child.badge}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      } else {
        const Icon = item.icon;
        const isActive = location.pathname === item.to;
        
        const handleClick = () => {
          setSidebarOpen(false);
        };
        
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={handleClick}
            className={`
              flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200
              ${isActive 
                ? 'bg-primary/10 text-primary font-medium' 
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'}
            `}
          >
            <Icon className="h-5 w-5" />
            <span className="font-medium">{item.label}</span>
          </Link>
        );
      }
    })
  );

  return (
    <div className="min-h-screen bg-background">
      {/* tawk.to deaktiviert – ersetzt durch Hilfe-Center + AI Chatbot */}
      {/* <TawkToChat /> */}

      {/* ── Floating FAB: Hilfe & Anleitungen (mobil) ──────────────────────── */}
      {/* iOS Safari: Button-Container DIREKT fixed (nur bottom/right), KEIN inset-Overlay
          (top:0+bottom:0), das iOS-WebKit beim Ein-/Ausblenden der Browserleiste falsch
          neu dimensioniert und den Button zur Mitte rutschen lässt. Gleicher Bodenabstand
          wie der Zurück-Button für konsistente Ausrichtung unten links/rechts. */}
      <div
        className="lg:hidden"
        style={{
          position: 'fixed',
          bottom: 'max(calc(env(safe-area-inset-bottom, 0px) + 16px), 20px)',
          right: '16px',
          zIndex: 9999,
        }}
      >
          {/* Ausgeklappte Optionen */}
          {fabOpen && (
            <div className="flex flex-col items-end gap-2 mb-2 animate-in slide-in-from-bottom-2 duration-150">
              {/* Hilfe-Center */}
              <a
                href="/help"
                onClick={() => setFabOpen(false)}
                className="flex items-center gap-2 bg-white dark:bg-gray-800 text-foreground border border-border text-sm font-medium px-3.5 py-2.5 rounded-full shadow-lg hover:bg-muted transition-all whitespace-nowrap"
                data-testid="fab-help-btn"
              >
                <BookOpen className="h-4 w-4 shrink-0 text-primary" />
                Hilfe & Anleitungen
              </a>
            </div>
          )}

          {/* FAB Hauptbutton */}
          <div className="relative">
            <button
              onClick={() => setFabOpen(v => !v)}
              className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all ${
                fabOpen ? 'bg-gray-700 rotate-45 scale-95' : 'bg-primary hover:bg-primary/90 hover:scale-110'
              } text-white`}
              data-testid="floating-fab-btn"
              aria-label="Hilfe & Support"
            >
              {fabOpen ? <X className="h-5 w-5" /> : <HelpCircle className="h-5 w-5" />}
            </button>
            {/* Roter Badge für ungelesene Support-Ticket-Antworten */}
            {!fabOpen && supportUnreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none pointer-events-none"
                data-testid="fab-support-badge"
              >
                {supportUnreadCount > 9 ? '9+' : supportUnreadCount}
              </span>
            )}
          </div>
      </div>

      {/* FAB Desktop (lg+): bleibt wie bisher mit fixed, da kein scroll-jump Problem auf Desktop */}
      <div
        className="hidden lg:block fixed z-40"
        style={{
          bottom: '20px',
          right: '16px',
        }}
      >
        {fabOpen && (
          <div className="flex flex-col items-end gap-2 mb-2 animate-in slide-in-from-bottom-2 duration-150">
            <a
              href="/help"
              onClick={() => setFabOpen(false)}
              className="flex items-center gap-2 bg-white dark:bg-gray-800 text-foreground border border-border text-sm font-medium px-3.5 py-2.5 rounded-full shadow-lg hover:bg-muted transition-all whitespace-nowrap"
            >
              <BookOpen className="h-4 w-4 shrink-0 text-primary" />
              Hilfe & Anleitungen
            </a>
          </div>
        )}
        <div className="relative">
          <button
            onClick={() => setFabOpen(v => !v)}
            className={`flex items-center justify-center w-12 h-12 rounded-full shadow-lg transition-all ${
              fabOpen ? 'bg-gray-700 rotate-45 scale-95' : 'bg-primary hover:bg-primary/90 hover:scale-110'
            } text-white`}
            data-testid="floating-fab-btn-desktop"
            aria-label="Hilfe & Support"
          >
            {fabOpen ? <X className="h-5 w-5" /> : <HelpCircle className="h-5 w-5" />}
          </button>
          {!fabOpen && supportUnreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none pointer-events-none">
              {supportUnreadCount > 9 ? '9+' : supportUnreadCount}
            </span>
          )}
        </div>
      </div>

      {/* Push Notification Permission Prompt */}
      <PushNotificationPrompt />

      {/* PWA Install Banner – nur wenn noch nicht installiert */}
      <PWAInstallBanner />

      {/* Impersonation Banner - ENTFERNT: Header wird jetzt direkt rot */}

      {/* Mobile Header – immer sichtbar, rot wenn im Impersonation-Mode */}
      <div
        className={`lg:hidden fixed left-0 right-0 z-40 border-b shadow-sm safe-top transition-colors ${
          isImpersonating ? 'bg-red-600 border-red-700' : 'bg-card border-border'
        }`}
        style={{
          top: 'var(--demo-banner-height, 0px)',
          paddingLeft: 'var(--safe-left)',
          paddingRight: 'var(--safe-right)',
        }}
      >
        <div className="flex items-center justify-between px-2 h-[56px]">
          {/* Left: Logo (always visible) */}
          <div className="flex items-center gap-1">
            <Link
              to={user?.role === 'employee' || user?.role === 'stationsleitung' ? '/employee/dashboard' : user?.role === 'superadmin' ? '/superadmin/dashboard' : '/admin'}
              data-testid="header-logo-link"
              className="flex items-center"
            >
              {/* Mobil: nur quadratisches Icon (Querlogo würde zu breit) */}
              <img src="/allflowx-icon.png" alt="AllFlowX" className="h-9 w-auto object-contain ml-1 sm:hidden" />
              {/* Ab sm: Querlogo, Theme-abhängig (ohne Reload via dark-Klasse) */}
              <span className="hidden sm:flex items-center ml-1">
                <img src="/allflowx-horizontal-light.png" alt="AllFlowX" className="h-10 lg:h-11 w-auto object-contain dark:hidden" />
                <img src="/allflowx-horizontal-dark.png" alt="AllFlowX" className="h-10 lg:h-11 w-auto object-contain hidden dark:block" />
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-1">
            {/* Geburtstags-Badge – IMMER sichtbar (Zahl nur bei Treffern im 30-Tage-Fenster) */}
            <button
              data-testid="birthday-badge-mobile"
              onClick={() => setShowBirthdayModal(true)}
              title={birthdays.length > 0 ? birthdays.map(b => b.is_today ? `${b.name} hat HEUTE Geburtstag!` : `${b.name} in ${b.days_until} Tag${b.days_until === 1 ? '' : 'en'}`).join(' · ') : 'Keine Geburtstage in den nächsten 30 Tagen'}
              className="relative p-2 rounded-xl text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
            >
              🎂
              {birthdays.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-yellow-400 text-yellow-900 text-[9px] font-bold rounded-full flex items-center justify-center">
                  {birthdays.length}
                </span>
              )}
            </button>
            {/* Zurück-Button NUR bei Impersonation, Glocke NUR ohne Impersonation */}
            {isImpersonating ? (
              <button
                onClick={handleExitImpersonation}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-sm font-medium transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </button>
            ) : (
              user && token && <NotificationBell token={token} user={user} />
            )}
            {/* Profile Dropdown — IMMER sichtbar (wie am Desktop) */}
            {user && (
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  className={isImpersonating ? 'text-white hover:bg-white/20' : 'text-foreground hover:bg-muted'}
                >
                  <User className="h-5 w-5" />
                </Button>
                <ProfileDropdown
                  user={user}
                  isOpen={showProfileDropdown}
                  onClose={() => setShowProfileDropdown(false)}
                  onLogout={logout}
                  currentLocation={currentLocationName}
                />
              </div>
            )}
            {/* Hamburger Menu — IMMER sichtbar (auch im Impersonation-Modus) */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className={isImpersonating ? 'text-white hover:bg-white/20' : 'text-foreground hover:bg-muted'}
              data-testid="hamburger-menu-btn"
            >
              {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 z-40 h-screen w-64
          bg-muted text-foreground
          shadow-lg border-r border-border
          transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{ top: isImpersonating ? 'calc(var(--safe-top) + 36px + var(--demo-banner-height, 0px))' : 'var(--demo-banner-height, 0px)' }}
      >
        <div className="flex flex-col h-full">
          {/* Logo Header */}
          <div
            className={`bg-card px-4 flex items-end pb-3 border-b border-border ${isImpersonating ? '' : 'safe-top'}`}
            style={{ minHeight: isImpersonating ? '56px' : 'calc(56px + var(--safe-top))' }}
          >
            <Link to={user?.role === 'employee' || user?.role === 'stationsleitung' ? '/employee/dashboard' : user?.role === 'superadmin' ? '/superadmin/dashboard' : '/admin/dashboard'} className="cursor-pointer">
              <img src="/allflowx-horizontal-light.png" alt="AllFlowX" className="h-11 w-auto object-contain dark:hidden" />
              <img src="/allflowx-horizontal-dark.png" alt="AllFlowX" className="h-11 w-auto object-contain hidden dark:block" />
            </Link>
          </div>

          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-sm text-foreground font-medium">{user?.name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {user?.role === 'superadmin' ? 'Superadmin' : user?.role === 'admin' ? 'Administrator' : user?.role === 'stationsleitung' ? 'Stationsleitung' : 'Mitarbeiter'}
            </p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navigation.type === 'simple' 
              ? renderSimpleLinks(navigation.links)
              : renderDropdownLinks(navigation.links)
            }
          </nav>

          {/* Verbesserungsvorschlag Button (for Admin and Employee) */}
          {user?.role !== 'superadmin' && (
            <div className="p-4 border-t border-border">
              <Button
                onClick={() => {
                  const route = user?.role === 'admin' 
                    ? '/admin/improvement-suggestions' 
                    : '/employee/improvement-suggestions';
                  navigate(route);
                  setSidebarOpen(false);
                }}
                variant="ghost"
                className="w-full justify-start text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200 relative"
              >
                <Lightbulb className="mr-3 h-5 w-5" />
                Verbesserungsvorschlag
                {user?.role === 'admin' && unreadSuggestionsCount > 0 && (
                  <span className="ml-auto inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-bold text-white bg-red-600 rounded-full shadow-lg">
                    {unreadSuggestionsCount > 9 ? '9+' : unreadSuggestionsCount}
                  </span>
                )}
              </Button>
            </div>
          )}

          {/* Logout Button Removed - Use Profile Dropdown instead */}
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Feedback Modal */}
      {feedbackOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <Lightbulb className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Verbesserungsvorschlag</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Haben Sie eine Idee, wie wir unsere App verbessern können? Teilen Sie uns Ihren Vorschlag mit!
            </p>
            <textarea
              value={feedbackMessage}
              onChange={(e) => setFeedbackMessage(e.target.value)}
              placeholder="Ihr Vorschlag..."
              className="w-full h-32 p-3 border border-border bg-background text-foreground rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <div className="flex gap-3 mt-4">
              <Button
                variant="outline"
                onClick={() => setFeedbackOpen(false)}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleSendFeedback}
                disabled={!feedbackMessage.trim() || feedbackSending}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                {feedbackSending ? 'Senden...' : 'Absenden'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Header - Only on large screens */}
      <div className={`hidden lg:block fixed right-0 left-64 z-30 border-b shadow-sm h-16 transition-colors ${
        isImpersonating ? 'bg-red-600 border-red-700' : 'bg-card border-border'
      }`}
        style={{ top: 'var(--demo-banner-height, 0px)' }}>
        <div className="flex items-center justify-between px-6 h-full">
          <div className="flex-1"></div>
          
          {/* Notifications & Profile Section */}
          <div className="flex items-center gap-3">
            {/* Impersonation: Zurück-Button LINKS neben Profil */}
            {isImpersonating && (
              <button
                onClick={handleExitImpersonation}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white text-red-600 font-semibold text-sm hover:bg-red-50 transition-colors border border-white/50"
              >
                <ArrowLeft className="h-4 w-4" />
                Zurück
              </button>
            )}
            {!isImpersonating && user && token && <NotificationBell token={token} user={user} />}
            {/* Geburtstags-Badge – IMMER sichtbar (Desktop) */}
            <button
              data-testid="birthday-badge-desktop"
              onClick={() => setShowBirthdayModal(true)}
              title={birthdays.length > 0 ? birthdays.map(b => b.is_today ? `${b.name} hat HEUTE Geburtstag!` : `${b.name} in ${b.days_until} Tag${b.days_until === 1 ? '' : 'en'}`).join(' · ') : 'Keine Geburtstage in den nächsten 30 Tagen'}
              className="relative p-2 rounded-xl text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
            >
              🎂
              {birthdays.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 w-4 bg-yellow-400 text-yellow-900 text-[9px] font-bold rounded-full flex items-center justify-center">
                  {birthdays.length}
                </span>
              )}
            </button>
            <div className="relative">
              <button
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                className={`flex items-center gap-2 sm:gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isImpersonating
                    ? 'hover:bg-white/20 text-white'
                    : 'hover:bg-muted'
                }`}
              >
                <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center text-white flex-shrink-0 ${
                  isImpersonating ? 'bg-white/30' : 'bg-gradient-to-r from-blue-600 to-blue-700'
                }`}>
                  <User className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div className="hidden sm:block text-left">
                  <p className={`text-sm font-medium ${isImpersonating ? 'text-white' : 'text-foreground'}`}>{user?.name}</p>
                  <p className={`text-xs ${isImpersonating ? 'text-white/70' : 'text-muted-foreground'}`}>
                    {user?.role === 'superadmin' ? 'Superadmin' :
                     user?.role === 'admin' ? 'Administrator' :
                     user?.role === 'stationsleitung' ? 'Stationsleitung' : 'Mitarbeiter'}
                  </p>
                </div>
                <ChevronDown className={`h-4 w-4 transition-transform ${showProfileDropdown ? 'rotate-180' : ''} ${isImpersonating ? 'text-white/70' : 'text-muted-foreground'}`} />
              </button>
              <ProfileDropdown
                user={user}
                isOpen={showProfileDropdown}
                onClose={() => setShowProfileDropdown(false)}
                onLogout={logout}
              />
            </div>
          </div>
        </div>
      </div>

      {/* BANNER SECTION - Direkt unter Topbar */}
      <div
        className={`lg:ml-64 lg:pt-16`}
        style={{
          paddingTop: 'calc(56px + var(--safe-top) + var(--demo-banner-height, 0px))'
        }}
      >
        {/* 🆕 Trial Banner - Shows trial countdown and payment status - ONLY FOR ADMIN */}
        {user && user.role === 'admin' && subscriptionStatus.subscription && (
          <TrialBanner subscription={subscriptionStatus.subscription} />
        )}

        {/* 🆕 Payment Status Banner - Shows warning for past_due/unpaid/suspended - ONLY FOR ADMIN */}
        {user && user.role === 'admin' && (
          <PaymentStatusBanner 
            billingStatus={subscriptionStatus.subscription?.billing_status}
            paymentFailedAt={subscriptionStatus.subscription?.payment_failed_at}
          />
        )}

        {/* 2FA Setup Warning Banner for Superadmin */}
        {user?.role === 'superadmin' && user?.must_setup_2fa && !user?.twofa_enabled && (
          <div className="bg-red-600 text-white px-4 py-3 flex items-center justify-between shadow-lg">
            <div className="flex items-center gap-3">
              <svg className="h-6 w-6 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="font-semibold text-sm">⚠️ Sicherheitswarnung: 2FA erforderlich!</p>
                <p className="text-xs opacity-90">Sie müssen 2FA aktivieren, um Ihren Superadmin-Account weiterhin nutzen zu können.</p>
              </div>
            </div>
            <a
              href="/superadmin/settings"
              className="bg-white text-red-600 px-4 py-2 rounded font-medium text-sm hover:bg-red-50 transition-colors whitespace-nowrap"
            >
              Jetzt einrichten
            </a>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main
        className={`lg:ml-64 min-h-screen ${isImpersonating ? 'lg:pt-28' : 'lg:pt-16'}`}
        style={{
          paddingTop: isImpersonating
            ? 'calc(var(--safe-top) + 36px + var(--demo-banner-height, 0px))'
            : 'calc(56px + var(--safe-top) + var(--demo-banner-height, 0px))'
        }}
      >
        <div className="p-4 sm:p-6">{children}</div>
        
        {/* Footer with Legal Links */}
        <footer className="mt-8 pt-6 border-t border-border" style={{ paddingBottom: 'calc(1.5rem + var(--safe-bottom))' }}>
          <div className="flex items-center justify-center space-x-4 text-sm text-muted-foreground">
            <Link 
              to="/impressum" 
              className="hover:text-foreground transition-colors"
            >
              Impressum
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link 
              to="/datenschutz" 
              className="hover:text-foreground transition-colors"
            >
              Datenschutz
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link 
              to="/agb" 
              className="hover:text-foreground transition-colors"
            >
              AGB
            </Link>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-3">
            © {new Date().getFullYear()} AllFlowX. Alle Rechte vorbehalten.
          </p>
          <p className="text-center text-[10px] text-muted-foreground/70 mt-1" data-testid="app-build-version">
            Build {buildInfo.commit} · {(buildInfo.builtAt || '').slice(0, 16).replace('T', ' ')}
          </p>
        </footer>
      </main>
      
      {/* Paywall Modal */}
      <PaywallModal 
        isOpen={showPaywallModal} 
        onClose={() => setShowPaywallModal(false)} 
      />

      {/* ── Eigene Geburtstags-Animation (für das Geburtstagskind selbst) ─── */}
      {showMyBirthdayFireworks && myBirthdayData && (
        <BirthdayFireworks
          names={[myBirthdayData.name]}
          age={myBirthdayData.age}
          daysSince={myBirthdayData.days_since || 0}
          isSelf={true}
          onClose={() => setShowMyBirthdayFireworks(false)}
        />
      )}

      {/* ── Geburtstags-Raketen-Animation (nur wenn heute Geburtstag) ─────── */}
      {showBirthdayFireworks && (
        <BirthdayFireworks
          names={birthdays.filter(b => b.is_today).map(b => b.name)}
          onClose={() => {
            setShowBirthdayFireworks(false);
            setShowBirthdayModal(true); // Danach das Info-Modal
          }}
        />
      )}

      {/* ── Geburtstags-Modal (3-Tage-Fenster, immer öffenbar) ──────────────── */}
      {showBirthdayModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowBirthdayModal(false)}>
          <div data-testid="birthday-modal" className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-sm w-full p-6 space-y-4"
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="text-center space-y-1">
              <div className="text-5xl">🎂</div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {birthdays.filter(b => b.is_today).length > 0
                  ? birthdays.filter(b => b.is_today).length === 1
                    ? 'Heute ist Geburtstag!'
                    : `${birthdays.filter(b => b.is_today).length} Kollegen haben heute Geburtstag!`
                  : 'Geburtstage'
                }
              </h2>
            </div>

            {/* Leer-Zustand */}
            {birthdays.length === 0 && (
              <p data-testid="birthday-empty" className="text-center text-sm text-gray-500 dark:text-gray-400 py-4">
                Keine Geburtstage in den nächsten 30 Tagen 🎈
              </p>
            )}

            {/* Heutige Geburtstage */}
            {birthdays.filter(b => b.is_today).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-yellow-600 uppercase tracking-wide">🎉 Heute</p>
                {birthdays.filter(b => b.is_today).map((b, i) => (
                  <div key={i} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-xl px-4 py-3 flex items-center gap-3">
                    <span className="text-2xl">🥳</span>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{b.name}</p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-400">hat heute Geburtstag!</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bevorstehende Geburtstage (Morgen / In 2 Tagen / In 3 Tagen) */}
            {birthdays.filter(b => !b.is_today).length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-blue-500 uppercase tracking-wide">📅 Bevorstehend</p>
                {birthdays.filter(b => !b.is_today).map((b, i) => (
                  <div key={i} className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-xl px-4 py-3 flex items-center gap-3">
                    <span className="text-2xl">🎁</span>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white">{b.name}</p>
                      <p className="text-sm text-blue-600 dark:text-blue-400">
                        {b.days_until === 1 ? 'Morgen' : `In ${b.days_until} Tagen`}
                        {b.birthday_date ? ` · ${b.birthday_date}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={() => setShowBirthdayModal(false)}
              className="w-full py-2.5 bg-yellow-400 hover:bg-yellow-500 text-yellow-900 font-semibold rounded-xl transition-colors"
            >
              Schließen 👍
            </button>
          </div>
        </div>
      )}

      {/* Lieferung Add-on Upsell Modal */}
      {showLieferungUpsell && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowLieferungUpsell(false)}
        >
          <div
            className="bg-card rounded-2xl shadow-2xl w-full max-w-md"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-5 border-b border-border flex items-center justify-between">
              <h2 className="font-bold text-lg text-foreground">Lieferschein-KI-Manager</h2>
              <button
                onClick={() => setShowLieferungUpsell(false)}
                className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-muted"
              >
                ✕
              </button>
            </div>
            <div className="p-5">
              <LieferungAddonCard />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
